import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
// `@hello-pangea/dnd`, NOT `react-beautiful-dnd` — and this is a correctness fix, not an upgrade.
//
// THE DEFECT (owner, 2026-09-01, twice): every drag threw
//
//     Invariant failed: Cannot find droppable entry with id [canvas]
//
// `react-beautiful-dnd@13.1.1` was archived in 2022 and does not support React 18's StrictMode,
// which this app uses (`index.tsx` wraps the tree in `<React.StrictMode>` under React 18.3.1).
// StrictMode double-invokes effects in development — mount, unmount, mount — and rbd 13's Droppable
// unregisters on that cleanup without re-registering against the live registry. The component stays
// mounted while the registry is empty, so the FIRST drag asks for `canvas` and finds nothing.
//
// It is development-only (a production build does not double-invoke), which is why it survived: the
// deployed app drags fine and nobody had dragged locally. It also has nothing to do with the change
// that preceded the report — I made the droppable unconditional first, on a real but different
// fragility, and the error persisted unchanged. That persistence is what ruled the structure out.
//
// `@hello-pangea/dnd` is rbd's maintained fork by its own community, with the SAME public API and
// React 18/19 in its peer range. The migration is the import specifier and nothing else — no prop,
// no callback, no id changes — which is why it is safe to do in one commit.
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';

export type StoredFormOption = string | { label: string; value: string };

export type EditableFormOption = {
  id: string;
  label: string;
  value: string;
};

let optionRowSequence = 0;

function nextRowId(fieldId: string): string {
  optionRowSequence += 1;
  return `form-option-${fieldId}-${optionRowSequence}`;
}

export function editableFormOptions(
  fieldId: string,
  options: unknown,
): EditableFormOption[] {
  if (!Array.isArray(options)) return [];
  return options.map((option) => {
    if (option && typeof option === 'object') {
      const record = option as Record<string, unknown>;
      const value = String(record.value ?? record.label ?? '');
      return {
        id: nextRowId(fieldId),
        label: String(record.label ?? value),
        value,
      };
    }
    const value = String(option ?? '');
    return { id: nextRowId(fieldId), label: value, value };
  });
}

export function storedFormOptions(rows: EditableFormOption[]): StoredFormOption[] {
  return rows.map(({ label, value }) => ({ label, value }));
}

export function moveFormOption(
  rows: EditableFormOption[],
  from: number,
  to: number,
): EditableFormOption[] {
  const moved = Array.from(rows);
  const [row] = moved.splice(from, 1);
  moved.splice(to, 0, row);
  return moved;
}

function nextOptionValue(rows: EditableFormOption[]): string {
  const used = new Set(rows.map((row) => row.value));
  let index = rows.length + 1;
  while (used.has(`option_${index}`)) index += 1;
  return `option_${index}`;
}

export default function FormOptionsEditor({
  fieldId,
  options,
  onChange,
}: {
  fieldId: string;
  options: unknown;
  onChange: (options: StoredFormOption[]) => void;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<EditableFormOption[]>(() => editableFormOptions(fieldId, options));
  const [showValues, setShowValues] = useState(false);

  const commit = (next: EditableFormOption[]) => {
    setRows(next);
    onChange(storedFormOptions(next));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    commit(moveFormOption(rows, result.source.index, result.destination.index));
  };

  const addOption = () => {
    const number = rows.length + 1;
    commit(rows.concat({
      id: nextRowId(fieldId),
      label: t('processes:wizardForm.optionDefaultLabel', { number }),
      value: nextOptionValue(rows),
    }));
  };

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {t('processes:wizardForm.optionsLabel')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('processes:wizardForm.optionsHelper')}
          </Typography>
        </Box>
        <Tooltip title={t('processes:wizardForm.savedValuesHelper')}>
          <IconButton
            size="small"
            color={showValues ? 'primary' : 'default'}
            aria-label={t('processes:wizardForm.showSavedValues')}
            aria-pressed={showValues}
            onClick={() => setShowValues((current) => !current)}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {rows.length === 0 ? (
        <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('processes:wizardForm.noOptions')}
          </Typography>
        </Box>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={`form-options-${fieldId}`}>
            {(dropProvided) => (
              <Stack
                ref={dropProvided.innerRef}
                {...dropProvided.droppableProps}
                spacing={0.75}
              >
                {rows.map((row, index) => (
                  <Draggable key={row.id} draggableId={row.id} index={index}>
                    {(dragProvided) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        sx={{
                          p: 0.75,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box
                            {...dragProvided.dragHandleProps}
                            role="button"
                            aria-label={t('processes:wizardForm.reorderOption', { number: index + 1 })}
                            sx={{ display: 'flex', color: 'text.disabled', cursor: 'grab', flexShrink: 0 }}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </Box>
                          <TextField
                            value={row.label}
                            label={t('processes:wizardForm.optionLabel', { number: index + 1 })}
                            onChange={(event) => commit(rows.map((candidate) => (
                              candidate.id === row.id
                                ? { ...candidate, label: event.target.value }
                                : candidate
                            )))}
                            size="small"
                            fullWidth
                            inputProps={{ 'aria-label': t('processes:wizardForm.optionLabel', { number: index + 1 }) }}
                          />
                          <Tooltip title={rows.length === 1
                            ? t('processes:wizardForm.keepOneOption')
                            : t('processes:wizardForm.deleteOption', { number: index + 1 })}
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={rows.length === 1}
                                aria-label={t('processes:wizardForm.deleteOption', { number: index + 1 })}
                                onClick={() => commit(rows.filter((candidate) => candidate.id !== row.id))}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        {showValues && (
                          <TextField
                            value={row.value}
                            label={t('processes:wizardForm.savedValue')}
                            size="small"
                            fullWidth
                            InputProps={{ readOnly: true }}
                            helperText={t('processes:wizardForm.savedValueReadOnly')}
                            sx={{ mt: 0.75, pl: 3.5, '& .MuiFormHelperText-root': { mx: 0 } }}
                          />
                        )}
                      </Box>
                    )}
                  </Draggable>
                ))}
                {dropProvided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={addOption}
        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
      >
        {t('processes:wizardForm.addOption')}
      </Button>
    </Stack>
  );
}
